/**
 * rs-product-sticky-cta.js
 *
 * Mobile sticky Add to Cart bar for the PDP.
 *  - Visibility  — appears once the main ATC button scrolls out of the top of the
 *                  viewport, hides again when the footer comes into view.
 *  - Button      — proxy for the real ATC button. Mirrors its text and disabled
 *                  state from `rs:atc-state`, forwards clicks to it so quantity,
 *                  line item properties and the charm multi-item builder all run
 *                  through theme's normal cart flow.
 *  - Gated state — when a customization gate (MTO / charm builder) is what
 *                  disabled the button, the proxy stays tappable and scrolls the
 *                  customer to the blocking block instead of sitting disabled.
 *  - Row 3       — live customization summary, mirroring the cart drawer: charm
 *                  thumbnails with "/" separators, or MTO field values.
 *
 * Loaded from rs-product-core.liquid when the section setting is enabled.
 */
if (!customElements.get('rs-product-sticky-cta')) {
  customElements.define('rs-product-sticky-cta', class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('[data-rs-sticky-atc]');
      this.buttonText = this.querySelector('[data-rs-sticky-atc-text]');
      if (!this.button || !this.buttonText || !this._customizationsElement()) return;

      this._passedButton = false;
      this._footerVisible = false;
      this._charms = [];
      this._state = null;
      this._mode = 'add';

      this._onAtcState = this._onAtcState.bind(this);
      this._onCustomizationChange = this._onCustomizationChange.bind(this);
      this._onCharmSlots = this._onCharmSlots.bind(this);
      this._onBreakpointChange = this._onBreakpointChange.bind(this);
      this._onClick = this._onClick.bind(this);

      const boot = () => this._init();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
      } else {
        boot();
      }
    }

    disconnectedCallback() {
      this._unobserve();
      this._heightObserver?.disconnect();
      this._buttonObserver?.disconnect();
      this._priceObserver?.disconnect();
      this._mediaQuery?.removeEventListener('change', this._onBreakpointChange);
      document.removeEventListener('rs:atc-state', this._onAtcState);
      document.removeEventListener('rs:properties-change', this._onCustomizationChange);
      document.removeEventListener('rs:charm-slots-change', this._onCharmSlots);
      document.body.removeAttribute('data-rs-sticky-cta-visible');
    }

    _init() {
      this._mediaQuery = window.matchMedia('(max-width: 749px)');
      this._mediaQuery.addEventListener('change', this._onBreakpointChange);

      this.button.addEventListener('click', this._onClick);
      document.addEventListener('rs:atc-state', this._onAtcState);
      document.addEventListener('rs:properties-change', this._onCustomizationChange);
      document.addEventListener('rs:charm-slots-change', this._onCharmSlots);

      this._observeButtonClasses();
      this._observeHeight();
      this._observeBundlePrice();

      this._render();
      this._renderCustomizations();
      this._applyBreakpoint();
    }

    /* ── Element lookups ────────────────────────── */

    _root() {
      return this.closest('product-info') || document.getElementById(`MainProduct--${this.sectionId}`);
    }

    _realButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    // Lives inside the data-rs-swap block, so it is replaced on every variant change — never cache the node.
    _customizationsElement() {
      return this.querySelector('[data-rs-sticky-customizations]');
    }

    _waitlistTrigger() {
      return this._root()?.querySelector('[data-rs-waitlist]') || null;
    }

    // The button itself is `hidden` in waitlist mode, so the wrapper is the reliable scroll trigger.
    _triggerElement() {
      const root = this._root();
      if (!root) return null;
      return root.querySelector('.product__submit__buttons') || this._realButton();
    }

    /* ── Visibility ─────────────────────────────── */

    _onBreakpointChange() {
      this._applyBreakpoint();
    }

    _applyBreakpoint() {
      if (this._mediaQuery.matches) {
        this._observe();
      } else {
        this._unobserve();
        this._passedButton = false;
      }
      this._updateVisibility();
    }

    _headerOffset() {
      if (!document.querySelector('[data-header-sticky]')) return 0;
      return document.querySelector('[data-header-height]')?.offsetHeight || 0;
    }

    _observe() {
      const trigger = this._triggerElement();
      if (trigger && !this._triggerObserver) {
        const offset = this._headerOffset();
        this._triggerObserver = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          this._passedButton = !entry.isIntersecting && entry.boundingClientRect.top < offset;
          this._updateVisibility();
        }, { threshold: 0, rootMargin: `-${offset}px 0px 0px 0px` });
        this._triggerObserver.observe(trigger);
      }

      const footer = document.querySelector('.footer-sections');
      if (footer && !this._footerObserver) {
        this._footerObserver = new IntersectionObserver((entries) => {
          this._footerVisible = entries[entries.length - 1].isIntersecting;
          this._updateVisibility();
        }, { threshold: 0 });
        this._footerObserver.observe(footer);
      }
    }

    _unobserve() {
      this._triggerObserver?.disconnect();
      this._footerObserver?.disconnect();
      this._triggerObserver = null;
      this._footerObserver = null;
    }

    _updateVisibility() {
      const visible = this._mediaQuery.matches && this._passedButton && !this._footerVisible;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');

      this._setBodyState('data-rs-sticky-cta-visible', visible);
    }

    _setBodyState(attribute, on) {
      if (on) {
        document.body.setAttribute(attribute, 'true');
      } else {
        document.body.removeAttribute(attribute);
      }
    }

    _observeHeight() {
      if (typeof ResizeObserver !== 'function') return;
      this._heightObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--rs-sticky-cta-height', `${this.offsetHeight}px`);
      });
      this._heightObserver.observe(this);
    }

    /* ── Button state ───────────────────────────── */

    _onAtcState(event) {
      const detail = event.detail;
      if (!detail || detail.sectionId !== this.sectionId) return;
      this._state = detail;
      this._render();
      this._renderCustomizations();
    }

    // Fallback for the window before the first rs:atc-state fires.
    _readRealButton() {
      const coreState = window.RSProduct?.getButtonState?.();
      if (coreState && coreState.sectionId === this.sectionId && coreState.text) return coreState;

      const real = this._realButton();
      if (!real) return { disabled: true, text: '', gated: false, gateId: null };

      const textElement = real.querySelector('[data-add-to-cart-text]');
      let text = '';
      if (textElement) {
        textElement.childNodes.forEach((node) => {
          if (node.nodeType === 3) text += node.textContent;
        });
      }
      return { disabled: real.hasAttribute('disabled'), text: text.trim(), gated: false, gateId: null };
    }

    _render() {
      const waitlist = this._waitlistTrigger();
      if (waitlist) {
        const label = waitlist.querySelector('.btn__text');
        this.buttonText.textContent = label
          ? label.textContent.trim()
          : (window.theme?.strings?.waitlist || 'Waitlist');
        this.button.removeAttribute('disabled');
        this.button.removeAttribute('data-rs-gated');
        this._mode = 'waitlist';
        return;
      }

      const state = this._state || this._readRealButton();
      const gated = !!(state.disabled && state.gated);

      this.buttonText.textContent = state.text || window.theme?.strings?.addToCart || 'Add to Cart';
      this.button.toggleAttribute('disabled', state.disabled && !gated);
      this.button.toggleAttribute('data-rs-gated', gated);
      this._mode = gated ? 'gated' : 'add';
    }

    // Mirror theme's own is-loading / is-added feedback onto the proxy.
    _observeButtonClasses() {
      const real = this._realButton();
      if (!real) return;
      this._buttonObserver = new MutationObserver(() => {
        this.button.classList.toggle('is-loading', real.classList.contains('is-loading'));
        this.button.classList.toggle('is-added', real.classList.contains('is-added'));
      });
      this._buttonObserver.observe(real, { attributes: true, attributeFilter: ['class'] });
    }

    /* ── Click ──────────────────────────────────── */

    _onClick(event) {
      event.preventDefault();

      if (this._mode === 'waitlist') {
        this._waitlistTrigger()?.click();
        return;
      }

      if (this._mode === 'gated') {
        this._scrollToGate();
        return;
      }

      const real = this._realButton();
      if (!real || real.hasAttribute('disabled')) return;
      real.click();
    }

    _scrollToGate() {
      const root = this._root();
      if (!root) return;

      const gateId = this._state?.gateId;
      let target = null;
      if (gateId === 'charm-builder') {
        target = root.querySelector('rs-product-charm-builder');
      } else if (gateId === 'mto-input') {
        target = root.querySelector('rs-product-mto-input:not(.hidden)');
      }
      if (!target) {
        target = root.querySelector('rs-product-mto-input:not(.hidden), rs-product-charm-builder')
          || root.querySelector('[data-form-wrapper]');
      }
      if (!target) return;

      const offset = target.getBoundingClientRect().top - 20;
      if (typeof window.theme?.scrollTo === 'function') {
        window.theme.scrollTo(offset);
      } else {
        window.scrollTo({ top: offset + window.scrollY, behavior: 'smooth' });
      }

      const fields = Array.from(target.querySelectorAll('[data-mto-field]'));
      const field = fields.find((input) => !input.value.trim()) || fields[0];
      if (field) window.setTimeout(() => field.focus({ preventScroll: true }), 400);
    }

    /* ── Customizations row ─────────────────────── */

    _onCustomizationChange(event) {
      if (event.detail && event.detail.sectionId !== this.sectionId) return;
      this._renderCustomizations();
    }

    _onCharmSlots(event) {
      const detail = event.detail;
      if (!detail || detail.sectionId !== this.sectionId) return;
      this._charms = detail.charms || [];
      this._slotLabel = detail.slotLabel || 'Charm';
      this._renderCustomizations();
    }

    // MTO values are read from the live inputs:
    // icon fields store an encoded cart value (#heart#), while the input holds the motif character the customer actually sees.
    _mtoValues() {
      const root = this._root();
      if (!root) return [];
      const fields = root.querySelectorAll('rs-product-mto-input:not(.hidden) [data-mto-field]');
      return Array.from(fields)
        .map((field) => field.value.trim())
        .filter(Boolean);
    }

    _renderCustomizations() {
      let html = '';

      if (this._charms.length) {
        html = this._charms
          .map((charm, index) => (index ? '<span class="rs-sticky-cta__sep">/</span>' : '') + this._charmHTML(charm, index))
          .join('');
      } else {
        const values = this._mtoValues();
        if (values.length) {
          html = `<span class="rs-sticky-cta__mto">${this._escape(values.join(' / '))}</span>`;
        }
      }

      const element = this._customizationsElement();
      if (!element) return;

      // Guard the write — rs:atc-state fires often and reassigning innerHTML would reload every charm thumbnail. 
      // Comparing against the live node rather than a cached string means a variant swap, which hands back an empty node, repaints.
      if (element.innerHTML === html) return;
      element.innerHTML = html;
      element.hidden = html === '';
    }

    // Slots keep their position: an empty slot renders a numbered placeholder 
    // so the customer can see it is #2 that still needs filling, not just "one is missing".
    _charmHTML(charm, index) {
      const position = index + 1;
      if (!charm) {
        const blankLabel = this._escape(`${this._slotLabel || 'Charm'} #${position} blank`);
        return `<span class="rs-sticky-cta__charm rs-sticky-cta__charm--empty" role="img" aria-label="${blankLabel}">${position}</span>`;
      }

      const label = this._escape(charm.label || charm.title || '');
      if (charm.imageUrl) {
        return `<span class="rs-sticky-cta__charm"><img src="${this._escape(charm.imageUrl)}" alt="${label}" width="22" height="22" loading="lazy"></span>`;
      }
      const initial = this._escape((charm.title || charm.label || '?').charAt(0));
      return `<span class="rs-sticky-cta__charm rs-sticky-cta__charm--text">${initial}</span>`;
    }

    _escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /* ── Charm bundle price ─────────────────────── */

    // Discount-tier charm products have their PDP price rewritten in JS; 
    // mirror it so the bar never shows a stale base price.
    _observeBundlePrice() {
      if (!this._root()?.querySelector('rs-product-charm-builder')) return;
      const priceElement = document.getElementById(`Price-${this.sectionId}`);
      if (!priceElement) return;

      this._priceObserver = new MutationObserver(() => this._syncBundlePrice());
      this._priceObserver.observe(priceElement, { childList: true, subtree: true, characterData: true });
      this._syncBundlePrice();
    }

    _syncBundlePrice() {
      if (!document.querySelector('[data-rs-charm-price-msg]')) return;

      const regular = document.getElementById(`Price-${this.sectionId}`)?.querySelector('.price-item--regular');
      const target = this.querySelector('.rs-sticky-cta__price .new-price');
      if (!regular || !target) return;

      const value = regular.textContent.trim();
      if (!value) return;

      target.textContent = value;
      this.querySelector('.rs-sticky-cta__price .old-price')?.remove();
      this.querySelector('.rs-sticky-cta__price .price')?.classList.remove('sale');
    }
  });
}
